"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  AGENTS_DEMO_ORG,
  type AgentsDemoOrg,
  type AgentsHumanRoleId,
  type OrgAgent,
  type AgentStatus,
  agentDirectory,
  connectionKindLabel,
  newAgentId,
  newConnectionId,
  newScheduleId,
  normalizeMailboxAddress,
  type ConnectionKind,
  type MailboxSource,
  type OrgConnection,
  type OrgSchedule,
  type ScheduleCadence,
} from "@/lib/agentsDemoData";
import {
  can,
  isViewOnly,
  primaryRoleLabel,
  type AgentsPermission,
} from "@/lib/agentsAccess";
import { useSession } from "@/auth/SessionProvider";
import {
  buildPersistedSnapshot,
  loadAgentsPersistedSnapshot,
  mergeOrgWithPersisted,
  saveAgentsPersistedSnapshot,
  type AgentsViewerMode,
} from "@/lib/agentsPersistence";
import {
  ensureAgentsOrgContext,
  peekAgentsOrgContext,
  resetAgentsOrgContextCache,
} from "@/lib/ensureAgentsOrgContext";
import { writeDashboardOrgCookie } from "@/lib/dashboardOrg";

export type { AgentsViewerMode };

export type AgentsOrgContextStatus = "loading" | "ready" | "missing";

type AgentsContextValue = {
  org: AgentsDemoOrg;
  /** Demo toggle: Super Admin vs Standard (view-only). */
  viewerMode: AgentsViewerMode;
  setViewerMode: (mode: AgentsViewerMode) => void;
  viewerRoles: AgentsHumanRoleId[];
  viewerLabel: string;
  viewOnly: boolean;
  may: (permission: AgentsPermission) => boolean;
  archiveAgent: (agentId: string) => void;
  hireAgent: (input: {
    name: string;
    ownerUserId: string;
    sharedWithUserIds: string[];
  }) => string | null;
  addConnection: (input: { name: string; kind: ConnectionKind; mail?: boolean }) => string | null;
  addSchedule: (input: { name: string; cadence: ScheduleCadence; note?: string }) => string | null;
  setAgentSchedules: (agentId: string, scheduleIds: string[]) => boolean;
  setAgentMailbox: (
    agentId: string,
    input: { mailboxAddress: string; mailboxSource: MailboxSource },
  ) => boolean;
  setConnectionStatus: (connectionId: string, status: OrgConnection["status"]) => void;
  getAgent: (agentId: string) => OrgAgent | undefined;
  persistenceReady: boolean;
  resetLocalDemo: () => void;
  /** Session / cookie / profile org resolved for suite APIs (knowledge, etc.). */
  orgContextStatus: AgentsOrgContextStatus;
  orgContextError: string | null;
  /** Stamp dashboard-org and update local org context without touching SSO session cookies. */
  applyActiveOrg: (orgId: string, companyName?: string | null) => void;
};

const AgentsContext = createContext<AgentsContextValue | null>(null);

export function AgentsContextProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const [org, setOrg] = useState<AgentsDemoOrg>(() =>
    mergeOrgWithPersisted(AGENTS_DEMO_ORG, loadAgentsPersistedSnapshot()),
  );
  const [viewerMode, setViewerModeState] = useState<AgentsViewerMode>(() => {
    const snap = loadAgentsPersistedSnapshot();
    return snap?.viewerMode ?? "super_admin";
  });
  const [persistenceReady, setPersistenceReady] = useState(false);
  const [orgContextStatus, setOrgContextStatus] = useState<AgentsOrgContextStatus>("loading");
  const [orgContextError, setOrgContextError] = useState<string | null>(null);

  const setViewerMode = useCallback((mode: AgentsViewerMode) => {
    setViewerModeState(mode);
  }, []);

  useEffect(() => {
    setPersistenceReady(true);
  }, []);

  useEffect(() => {
    if (!persistenceReady) return;
    saveAgentsPersistedSnapshot(buildPersistedSnapshot(org, viewerMode));
  }, [org, viewerMode, persistenceReady]);

  useEffect(() => {
    if (session.status !== "authenticated") {
      resetAgentsOrgContextCache();
      setOrgContextStatus(session.status === "loading" ? "loading" : "missing");
      setOrgContextError(null);
      return;
    }

    let cancelled = false;
    if (!peekAgentsOrgContext()?.orgId) {
      setOrgContextStatus("loading");
      setOrgContextError(null);
    }
    void (async () => {
      try {
        const result = await ensureAgentsOrgContext(session.data?.user?.orgId);
        if (cancelled) return;
        if (result.orgId) {
          setOrg((prev) => ({
            ...prev,
            name: result.companyName || prev.name,
            orgId: result.orgId || prev.orgId,
          }));
          setOrgContextStatus("ready");
          setOrgContextError(null);
        } else {
          setOrgContextStatus("missing");
          setOrgContextError(result.error || "Organization context required.");
        }
      } catch (e) {
        if (cancelled) return;
        setOrgContextStatus("missing");
        setOrgContextError(e instanceof Error ? e.message : "Could not resolve organization.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session.status, session.data?.user?.orgId]);

  const viewerRoles: AgentsHumanRoleId[] = useMemo(
    () => (viewerMode === "super_admin" ? ["super_admin", "standard"] : ["standard"]),
    [viewerMode],
  );

  const may = useCallback(
    (permission: AgentsPermission) => can(viewerRoles, permission),
    [viewerRoles],
  );

  const archiveAgent = useCallback(
    (agentId: string) => {
      if (!can(viewerRoles, "create_edit_archive_agents")) return;
      setOrg((prev) => ({
        ...prev,
        agents: prev.agents.map((agent) =>
          agent.id === agentId && agent.status !== "archived"
            ? ({ ...agent, status: "archived" as AgentStatus })
            : agent,
        ),
      }));
    },
    [viewerRoles],
  );

  const hireAgent = useCallback(
    (input: { name: string; ownerUserId: string; sharedWithUserIds: string[] }) => {
      if (!can(viewerRoles, "create_edit_archive_agents")) return null;
      const name = input.name.trim();
      const people = agentDirectory(org.users, session.data?.user);
      const owner = people.find((person) => person.id === input.ownerUserId);
      if (!name || !owner) return null;

      const sharedWithUserIds = [...new Set(input.sharedWithUserIds)].filter(
        (id) => id !== owner.id && people.some((person) => person.id === id),
      );
      const id = newAgentId(name);
      const hired: OrgAgent = {
        id,
        name,
        status: "active",
        humanOwnerUserId: owner.id,
        humanOwnerName: owner.name,
        humanOwnerEmail: owner.email,
        sharedWithUserIds,
        mailboxAddress: null,
        mailboxSource: null,
        scheduleIds: [],
        lastActiveAt: new Date().toISOString(),
        creditsBurnedMonth: 0,
      };
      setOrg((prev) => ({
        ...prev,
        agents: [hired, ...prev.agents],
      }));
      return id;
    },
    [viewerRoles, org.users, session.data?.user],
  );

  const addConnection = useCallback(
    (input: { name: string; kind: ConnectionKind; mail?: boolean }) => {
      if (!can(viewerRoles, "manage_connections")) return null;
      const name = input.name.trim();
      if (!name) return null;

      const id = newConnectionId(name);
      const added: OrgConnection = {
        id,
        name,
        kind: input.kind,
        provider: connectionKindLabel(input.kind),
        status: "not_connected",
        note: "",
        ...(input.mail ? { mail: true } : {}),
      };
      setOrg((prev) => ({
        ...prev,
        connections: [added, ...prev.connections],
      }));
      return id;
    },
    [viewerRoles],
  );

  const addSchedule = useCallback(
    (input: { name: string; cadence: ScheduleCadence; note?: string }) => {
      if (!can(viewerRoles, "manage_schedules")) return null;
      const name = input.name.trim();
      if (!name) return null;
      const note = input.note?.trim() ?? "";
      const id = newScheduleId(name);
      const added: OrgSchedule = {
        id,
        name,
        cadence: input.cadence,
        ...(note ? { note } : {}),
      };
      setOrg((prev) => ({
        ...prev,
        schedules: [added, ...prev.schedules],
      }));
      return id;
    },
    [viewerRoles],
  );

  const setAgentSchedules = useCallback(
    (agentId: string, scheduleIds: string[]) => {
      if (!can(viewerRoles, "manage_agent_configuration")) return false;
      setOrg((prev) => {
        if (!prev.agents.some((agent) => agent.id === agentId)) return prev;
        const allowed = new Set(prev.schedules.map((schedule) => schedule.id));
        const next = [...new Set(scheduleIds)].filter((id) => allowed.has(id));
        return {
          ...prev,
          agents: prev.agents.map((agent) =>
            agent.id === agentId ? { ...agent, scheduleIds: next } : agent,
          ),
        };
      });
      return true;
    },
    [viewerRoles],
  );

  const setAgentMailbox = useCallback(
    (agentId: string, input: { mailboxAddress: string; mailboxSource: MailboxSource }) => {
      if (!can(viewerRoles, "manage_agent_configuration")) return false;
      const mailboxAddress = normalizeMailboxAddress(input.mailboxAddress);
      if (!mailboxAddress) return false;
      setOrg((prev) => {
        if (!prev.agents.some((agent) => agent.id === agentId)) return prev;
        return {
          ...prev,
          agents: prev.agents.map((agent) =>
            agent.id === agentId
              ? { ...agent, mailboxAddress, mailboxSource: input.mailboxSource }
              : agent,
          ),
        };
      });
      return true;
    },
    [viewerRoles],
  );

  const setConnectionStatus = useCallback(
    (connectionId: string, status: OrgConnection["status"]) => {
      if (!can(viewerRoles, "manage_connections")) return;
      setOrg((prev) => ({
        ...prev,
        connections: prev.connections.map((c) =>
          c.id === connectionId ? { ...c, status } : c,
        ),
      }));
    },
    [viewerRoles],
  );

  const resetLocalDemo = useCallback(() => {
    setOrg(AGENTS_DEMO_ORG);
    setViewerModeState("super_admin");
    saveAgentsPersistedSnapshot(buildPersistedSnapshot(AGENTS_DEMO_ORG, "super_admin"));
  }, []);

  const applyActiveOrg = useCallback((orgId: string, companyName?: string | null) => {
    const id = orgId.trim();
    if (!id) return;
    writeDashboardOrgCookie(id);
    resetAgentsOrgContextCache();
    setOrg((prev) => ({
      ...prev,
      orgId: id,
      name: companyName?.trim() || prev.name,
    }));
    setOrgContextStatus("ready");
    setOrgContextError(null);
  }, []);

  const getAgent = useCallback(
    (agentId: string) => org.agents.find((agent) => agent.id === agentId),
    [org.agents],
  );

  const value: AgentsContextValue = {
    org,
    viewerMode,
    setViewerMode,
    viewerRoles,
    viewerLabel: primaryRoleLabel(viewerRoles),
    viewOnly: isViewOnly(viewerRoles),
    may,
    archiveAgent,
    hireAgent,
    addConnection,
    addSchedule,
    setAgentSchedules,
    setAgentMailbox,
    setConnectionStatus,
    getAgent,
    persistenceReady,
    resetLocalDemo,
    orgContextStatus,
    orgContextError,
    applyActiveOrg,
  };

  return <AgentsContext.Provider value={value}>{children}</AgentsContext.Provider>;
}

export function useAgents(): AgentsContextValue {
  const ctx = useContext(AgentsContext);
  if (!ctx) throw new Error("useAgents must be used within AgentsContextProvider");
  return ctx;
}
